import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import StructuredData from '@/components/base/StructuredData';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMsg('提交成功！我们会尽快回复您。');
      form.reset();
      return;
    }

    setFormStatus('loading');
    const formData = new FormData(form);

    try {
      const res = await fetch('http://lhrkj3.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(text); } catch { parsed = {}; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMsg('提交成功！感谢您的留言，我们会尽快回复。');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormMsg(msg);
      }
    } catch {
      setFormStatus('error');
      setFormMsg('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <StructuredData type="Organization" />
      <StructuredData type="WebSite" />
      <StructuredData
        type="WebPage"
        data={{
          name: '关于韩漫屋 - 韩漫在线阅读平台',
          description: '了解韩漫屋的品牌故事、发展历程与联系方式。韩漫屋致力于为韩漫爱好者提供免费、优质的韩漫在线阅读服务。',
        }}
      />
      <StructuredData type="BreadcrumbList" />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative w-full min-h-[50vh] flex items-center overflow-hidden">
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(135deg, oklch(0.06 0.002 275) 0%, oklch(0.10 0.015 25) 60%, oklch(0.14 0.04 40) 100%)',
              }}
            ></div>

            <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-24 pb-12">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap mb-6" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-400 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line text-foreground-600"></i>
                <span className="text-primary-400">关于我们</span>
              </nav>

              <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-16">
                <div className="lg:w-[60%] space-y-5">
                  <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-accent-500/10 text-accent-400 text-xs font-medium rounded-full border border-accent-500/20">
                    <i className="ri-building-line text-sm"></i>
                    品牌故事
                  </span>

                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-50 leading-tight">
                    关于
                    <span className="text-primary-500">韩漫屋</span>
                  </h1>

                  <p className="text-sm md:text-base text-foreground-300 max-w-lg leading-relaxed">
                    韩漫屋诞生于一群韩漫爱好者的共同梦想 — 让更多中文读者能够便捷地阅读到高质量的韩国漫画。我们坚持<strong className="text-primary-400 font-medium">免费、无删减</strong>的原则，为每一位读者保留漫画最原始的创作风貌。
                  </p>
                </div>

                <div className="lg:w-[40%] flex items-start justify-center lg:justify-end mt-4 lg:mt-0">
                  <div className="relative w-full max-w-sm">
                    <div className="absolute -inset-1 bg-primary-500/10 rounded-2xl blur-2xl"></div>
                    <div className="relative rounded-2xl overflow-hidden border border-background-200/30 aspect-[16/10]">
                      <img
                        src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20illustration%20of%20creative%20team%20working%20together%20in%20modern%20studio%2C%20warm%20cozy%20atmosphere%2C%20collaborative%20environment%2C%20anime%20art%20style%2C%20professional%20yet%20friendly%20vibe%2C%20clean%20composition%2C%20soft%20lighting&width=800&height=500&seq=about-team&orientation=landscape"
                        alt="韩漫屋团队"
                        title="韩漫屋团队"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="flex flex-col lg:flex-row gap-6 mb-14">
                <div className="lg:w-[40%] relative rounded-2xl overflow-hidden border border-background-200/30 aspect-[4/3]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20illustration%20of%20diverse%20readers%20enjoying%20comics%20on%20various%20devices%2C%20warm%20atmospheric%20lighting%2C%20modern%20cozy%20setting%2C%20anime%20art%20style%2C%20inclusive%20diverse%20scene%2C%20clean%20composition&width=800&height=600&seq=about-mission&orientation=landscape"
                    alt="韩漫屋使命"
                    title="韩漫屋 - 我们的使命"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="lg:w-[60%] space-y-5 flex flex-col justify-center">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-50">
                    我们的<span className="text-primary-500">使命</span>
                  </h2>
                  <p className="text-sm md:text-base text-foreground-300 leading-relaxed">
                    韩漫屋始终秉持「让好漫画触手可及」的理念。我们与多家韩国漫画平台和出版社保持合作，确保平台上的每一部作品都经过合法授权。我们相信，优质的漫画内容应该被更多人看到，而不应被语言和地域所限制。
                  </p>
                  <p className="text-sm md:text-base text-foreground-400 leading-relaxed">
                    从2024年上线至今，韩漫屋已经收录了超过800部韩国漫画作品，累计服务超过500万中文读者。我们采用<strong className="text-primary-400 font-medium">下拉式阅读</strong>模式，让读者在PC和手机端都能获得流畅的阅读体验。
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="bg-background-100 rounded-2xl p-6 border border-background-200/30 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary-500/10 flex items-center justify-center">
                    <i className="ri-book-open-line text-xl text-primary-400"></i>
                  </div>
                  <p className="text-3xl font-bold text-foreground-50 mb-1">800+</p>
                  <p className="text-xs text-foreground-400">收录漫画作品</p>
                </div>
                <div className="bg-background-100 rounded-2xl p-6 border border-background-200/30 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-accent-500/10 flex items-center justify-center">
                    <i className="ri-user-heart-line text-xl text-accent-400"></i>
                  </div>
                  <p className="text-3xl font-bold text-foreground-50 mb-1">500万+</p>
                  <p className="text-xs text-foreground-400">累计服务读者</p>
                </div>
                <div className="bg-background-100 rounded-2xl p-6 border border-background-200/30 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-secondary-500/10 flex items-center justify-center">
                    <i className="ri-thumb-up-line text-xl text-secondary-400"></i>
                  </div>
                  <p className="text-3xl font-bold text-foreground-50 mb-1">98%</p>
                  <p className="text-xs text-foreground-400">读者好评率</p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <span className="w-2 h-2 rounded-full bg-primary-500"></span>
                  <span className="text-xs font-medium text-primary-400 uppercase tracking-wider">口碑声誉</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-50">
                  读者信赖之选
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {[
                  {
                    name: '漫画迷小兔',
                    avatar: 'https://readdy.ai/api/search-image?query=Cute%20anime%20style%20girl%20avatar%20with%20pink%20hair%2C%20simple%20portrait%2C%20clean%20background%2C%20kawaii%20style&width=100&height=100&seq=about-avatar-01&orientation=squarish',
                    text: '韩漫屋是我每天必刷的漫画平台！更新速度超快，画质也很清晰。最重要的是完全免费，对漫画党太友好了！',
                    role: '资深韩漫读者',
                  },
                  {
                    name: '追漫小王子',
                    avatar: 'https://readdy.ai/api/search-image?query=Cool%20anime%20style%20boy%20avatar%20with%20dark%20hair%2C%20simple%20portrait%2C%20clean%20background%2C%20modern%20style&width=100&height=100&seq=about-avatar-02&orientation=squarish',
                    text: '用了韩漫屋之后就把其他漫画APP都卸载了。下拉式阅读体验太舒服了，而且分类很全，总能找到喜欢的。',
                    role: '韩漫爱好者',
                  },
                  {
                    name: '深海蓝鲸',
                    avatar: 'https://readdy.ai/api/search-image?query=Anime%20style%20intellectual%20avatar%20with%20glasses%2C%20simple%20portrait%2C%20clean%20background%2C%20smart%20style&width=100&height=100&seq=about-avatar-03&orientation=squarish',
                    text: '作为一个漫画创作者，我很欣赏韩漫屋对版权的重视。平台上的作品质量很高，社区氛围也很好。',
                    role: '漫画创作者',
                  },
                ].map((item, i) => (
                  <div key={i} className="bg-background-50 rounded-2xl p-5 border border-background-200/30">
                    <div className="flex items-center gap-0.5 mb-3">
                      {Array.from({ length: 5 }).map((_, j) => (
                        <i key={j} className="ri-star-fill text-sm text-accent-500"></i>
                      ))}
                    </div>
                    <p className="text-foreground-300 text-sm leading-relaxed mb-4">{item.text}</p>
                    <div className="flex items-center gap-3 pt-3 border-t border-background-200/30">
                      <div className="w-9 h-9 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                        <img src={item.avatar} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="text-foreground-200 text-xs font-medium">{item.name}</p>
                        <p className="text-foreground-600 text-[11px]">{item.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <div className="relative w-full h-48 md:h-56 rounded-2xl overflow-hidden border border-background-200/30 mb-10">
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20illustration%20of%20warm%20friendly%20customer%20service%20scene%2C%20two%20characters%20communicating%20happily%2C%20warm%20orange%20ambient%20lighting%2C%20anime%20art%20style%2C%20cozy%20atmosphere%2C%20cinematic%20composition%2C%20dark%20background&width=1600&height=400&seq=about-contact-banner&orientation=landscape"
                    alt="联系我们"
                    title="韩漫屋 - 联系我们"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-background-950/40"></div>
                </div>

                <h2 className="text-2xl md:text-3xl font-bold text-foreground-50 mb-3">
                  与 <span className="text-primary-500 italic">韩漫屋</span> 取得联系
                </h2>
                <p className="text-sm text-foreground-400 max-w-md mx-auto">
                  无论您是想合作、投稿、反馈问题，还是单纯想和我们聊聊漫画，都欢迎随时联系我们。
                </p>
              </div>

              <div className="max-w-2xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="honeypot-field">
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-foreground-400 mb-1.5">姓名</label>
                        <input
                          type="text"
                          name="name"
                          required
                          className="w-full bg-background-100 border border-background-200/30 rounded-xl px-4 py-2.5 text-sm text-foreground-200 outline-none transition-colors focus:border-primary-500/50 placeholder:text-foreground-600"
                          placeholder="您的姓名"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-foreground-400 mb-1.5">邮箱</label>
                        <input
                          type="email"
                          name="email"
                          required
                          className="w-full bg-background-100 border border-background-200/30 rounded-xl px-4 py-2.5 text-sm text-foreground-200 outline-none transition-colors focus:border-primary-500/50 placeholder:text-foreground-600"
                          placeholder="hello@lhrkj3.com"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-foreground-400 mb-1.5">主题</label>
                      <input
                        type="text"
                        name="subject"
                        required
                        className="w-full bg-background-100 border border-background-200/30 rounded-xl px-4 py-2.5 text-sm text-foreground-200 outline-none transition-colors focus:border-primary-500/50 placeholder:text-foreground-600"
                        placeholder="留言主题"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-foreground-400 mb-1.5">留言内容</label>
                      <textarea
                        name="message"
                        required
                        rows={5}
                        maxLength={500}
                        className="w-full bg-background-100 border border-background-200/30 rounded-xl px-4 py-2.5 text-sm text-foreground-200 outline-none transition-colors focus:border-primary-500/50 placeholder:text-foreground-600 resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full px-6 py-3 text-sm font-semibold bg-primary-500 text-background-950 rounded-full whitespace-nowrap transition-all duration-200 hover:bg-primary-400 cursor-pointer disabled:opacity-60"
                    >
                      {formStatus === 'loading' ? '提交中...' : '发送留言'}
                    </button>
                    {formStatus !== 'idle' && (
                      <p className={`text-xs text-center ${formStatus === 'success' ? 'text-primary-400' : 'text-red-400'}`}>
                        {formMsg}
                      </p>
                    )}
                  </form>
                </div>

                <div className="lg:col-span-2 space-y-6 pt-4">
                  <div>
                    <p className="text-sm font-medium text-foreground-200 mb-2">联系方式</p>
                    <div className="space-y-3">
                      <a href="mailto:hello@lhrkj3.com" className="flex items-center gap-3 text-sm text-foreground-400 hover:text-primary-400 transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                          <i className="ri-mail-line text-primary-400"></i>
                        </div>
                        hello@lhrkj3.com
                      </a>
                      <div className="flex items-center gap-3 text-sm text-foreground-400">
                        <div className="w-8 h-8 rounded-lg bg-accent-500/10 flex items-center justify-center flex-shrink-0">
                          <i className="ri-map-pin-line text-accent-400"></i>
                        </div>
                        中国 · 北京
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-foreground-200 mb-2">关注我们</p>
                    <div className="flex items-center gap-3">
                      <a href="#" className="w-9 h-9 rounded-lg bg-background-100 border border-background-200/30 flex items-center justify-center text-foreground-400 hover:text-primary-400 hover:border-primary-500/30 transition-all" aria-label="微博">
                        <i className="ri-weibo-line"></i>
                      </a>
                      <a href="#" className="w-9 h-9 rounded-lg bg-background-100 border border-background-200/30 flex items-center justify-center text-foreground-400 hover:text-primary-400 hover:border-primary-500/30 transition-all" aria-label="微信">
                        <i className="ri-wechat-line"></i>
                      </a>
                      <a href="#" className="w-9 h-9 rounded-lg bg-background-100 border border-background-200/30 flex items-center justify-center text-foreground-400 hover:text-primary-400 hover:border-primary-500/30 transition-all" aria-label="B站">
                        <i className="ri-bilibili-line"></i>
                      </a>
                      <a href="#" className="w-9 h-9 rounded-lg bg-background-100 border border-background-200/30 flex items-center justify-center text-foreground-400 hover:text-primary-400 hover:border-primary-500/30 transition-all" aria-label="抖音">
                        <i className="ri-tiktok-line"></i>
                      </a>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-foreground-200 mb-2">合作与投稿</p>
                    <p className="text-xs text-foreground-500 leading-relaxed">
                      如果您是漫画创作者或出版社，欢迎通过上方表单或邮箱与我们洽谈合作。我们始终欢迎优质的原创韩漫内容。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}